import React from 'react'
import ReactDOM from 'react-dom'
import { flowRight } from 'lodash'
import { withTranslationCustom } from 'lib/i18next'
import { withRouter } from 'react-router-dom'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import * as Yup from 'yup'
import * as Nope from 'lib/utils/validation/Nope'
import { PropTypes } from 'prop-types'
import { AD_STATUS } from 'lib/ad'
import { VEHICLE_SALES_METHOD, getVehicleVertical } from 'lib/utils/store/vehicle'
import moment from 'moment'

import { Field, Form } from 'formik'
import AdStatusLogic from 'components/Ads/AdStatusLogic'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import AdStatusSelect from 'components/Ads/AdStatusSelect/AdStatusSelect'
import Checkbox from 'components/Forms/FormElements/Checkbox'
import Button from 'components/UI/Button'
import ErrorComponent from 'components/Errors/ErrorComponent'
import AdStatusSoldForm from 'components/Ads/AdStatusSoldForm/AdStatusSoldForm'

import './ad-status-form.pcss'

class AdStatusForm extends React.Component {
    constructor (props) {
        super(props)

        this.state = {
            isFormSecondPart: false,
        }

        this.defaultTitle = props.t('vehicles:adStatusModal.title')
        this.secondPartTitle = props.t('vehicles:adStatusModal.titleSecondPart')

        this.setTitleText(this.defaultTitle)
    }

    handleFormPartChange = () => {
        const { isFormSecondPart } = this.state
        this.setTitleText(!isFormSecondPart ? this.defaultTitle : this.secondPartTitle)
    }

    setTitleText = (title) => {
        const { setTitle } = this.props
        setTitle && setTitle(title)
    }

    getAdStatusOptions () {
        const { t, vehicle } = this.props

        const vertical = getVehicleVertical(vehicle)

        return [
            {
                value: AD_STATUS.SOLD,
                name: t('verticalAds:adStatusOptions.sold.name', {
                    vertical: t(`vehicles:vertical.${vertical}.name`),
                }),
                description: t('verticalAds:adStatusOptions.sold.description'),
            },
            {
                value: AD_STATUS.REMOVED,
                name: t('verticalAds:adStatusOptions.removed.name'),
                description: t('verticalAds:adStatusOptions.removed.description'),
            },
        ]
    }

    getInitialValues () {
        const { adStatus } = this.props
        return {
            adStatus,
            soldViaVertical: true,
            price: '',
            date: new Date(),
            salesMethod: VEHICLE_SALES_METHOD.DIRECT,
            soldComment: '',
            soldOwnComment: '',
        }
    }

    getFormSchema () {
        const { isFormSecondPart } = this.state
        const { t } = this.props

        if (isFormSecondPart) {
            return Nope.schema({
                price: Nope.positiveInteger()
                    .required(t('formElements:validation.required')),
                date: Yup.string(), // @TODO: bug in formik, has to be string for now. https://github.com/jaredpalmer/formik/issues/1945. Should be fixed in version 2.04
                salesMethod: Yup.string(),
                soldComment: Yup.string(),
                soldOwnComment: Yup.string(),
            })
        }

        return Nope.schema({
            adStatus: Yup.string().required(),
            soldViaVertical: Yup.bool(),
        })
    }

    renderAdStatusOptionContent = (option, selected) => {
        const { t, vehicle } = this.props
        if (selected && option.value === AD_STATUS.SOLD) {
            const vertical = getVehicleVertical(vehicle)

            return (
                <Field name="soldViaVertical">
                    {(props) => (
                        <Checkbox
                            {...props}
                            label={t('verticalAds:form.soldViaVertical', {
                                vertical: t(`vehicles:vertical.${vertical}.name`),
                            })}
                        />
                    )}
                </Field>
            )
        }

        return null
    }

    renderButtons (submitForm, isSubmitting, onCancel, values, setFieldValue) {
        const { isFormSecondPart } = this.state
        const { t } = this.props

        const SubmitButton = (submitForm) => (
            <Button
                trackName="submit"
                type="submit"
                className="button"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={submitForm}
            >
                {t('common:save')}
            </Button>
        )

        const ContinueButton = (
            <Button
                trackName="continue"
                type="button"
                className="button"
                onClick={(e) => {
                    e.preventDefault()
                    this.setState({
                        isFormSecondPart: true,
                    }, this.handleFormPartChange)
                }}
            >
                {t('common:continue')}
            </Button>
        )

        return (
            <>
                {(values.adStatus === AD_STATUS.SOLD && !isFormSecondPart) ? (
                    ContinueButton
                ) : (
                    SubmitButton(submitForm)
                )}

                {!!onCancel && (
                    <Button
                        trackName="cancel"
                        type="button"
                        className="button button--secondary"
                        onClick={(e) => {
                            e.preventDefault()

                            if (isFormSecondPart) {
                                setFieldValue('adStatus', '')
                                this.setState({
                                    isFormSecondPart: false,
                                }, this.handleFormPartChange)
                                return
                            }

                            onCancel && onCancel()
                        }}
                    >
                        {t('common:cancel')}
                    </Button>
                )}
            </>
        )
    }

    renderForm (adStatusLogic) {
        const { onCancel, onSubmitted, renderButtonsPortalTarget } = this.props
        const { isFormSecondPart } = this.state

        return (
            <ExtendedFormik
                id="ad-status-form"
                initialValues={this.getInitialValues()}
                validationSchema={this.getFormSchema()}
                onSubmit={async (values, actions) => {
                    const vehicle = this.props.vehicle
                    const {
                        adStatus,
                        soldViaVertical,
                        price,
                        date,
                        salesMethod,
                        soldOwnComment,
                        soldComment,
                        oldStatusOption,
                        other
                    } = values

                    const statusInfo = {
                        soldPrice: Number.parseInt(price || 0),
                        soldDate: date ? moment(date).format('YYYY-MM-DD') : null,
                        salesMethod: salesMethod,
                        soldCommentForUser: soldOwnComment,
                        soldComment: soldComment,
                        soldThroughNettix: soldViaVertical,
                        oldComment: oldStatusOption === 'other' ? other : oldStatusOption
                    }

                    await adStatusLogic.setAdStatus(vehicle.id, adStatus, statusInfo)
                        .then(() => {
                            actions.setSubmitting(false)
                            onSubmitted && onSubmitted()
                        })
                        .catch(() => {
                            actions.setSubmitting(false)
                        })
                }}
            >
                {({ extendedActions, submitForm, isSubmitting, setFieldValue, values }) => (
                    <Form className="ad-status-form">
                        <FormElementGroup>
                            {isFormSecondPart ? (
                                <AdStatusSoldForm
                                    setFieldValue={setFieldValue}
                                    focusFirst={extendedActions.focusFirst}
                                />
                            ) : (
                                <Field name="adStatus">
                                    {(props) => (
                                        <AdStatusSelect
                                            {...props}
                                            type="radio"
                                            options={this.getAdStatusOptions()}
                                            renderContent={this.renderAdStatusOptionContent}
                                            onChange={() => adStatusLogic.clearErrors()}
                                        />
                                    )}
                                </Field>
                            )}
                            {adStatusLogic.error && <ErrorComponent error={adStatusLogic.error} />}

                            {renderButtonsPortalTarget && (
                                ReactDOM.createPortal(
                                    this.renderButtons(submitForm, isSubmitting, onCancel, values, setFieldValue),
                                    renderButtonsPortalTarget
                                )
                            )}
                            <div className="action-wrapper display-s-up">
                                {this.renderButtons(submitForm, isSubmitting, onCancel, values, setFieldValue)}
                            </div>
                        </FormElementGroup>
                    </Form>
                )}
            </ExtendedFormik>
        )
    }

    render () {
        return (
            <AdStatusLogic>
                {(adStatusLogic) => this.renderForm(adStatusLogic)}
            </AdStatusLogic>
        )
    }
}

AdStatusForm.propTypes = {
    vehicle: PropTypes.object.isRequired,
    adStatus: PropTypes.string.isRequired,
    setTitle: PropTypes.func,
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
    withTranslationCustom(['common', 'verticalAds', 'formElements'])
)(AdStatusForm)
